import { Component, ElementRef, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, take, startWith, tap, filter } from 'rxjs/operators';
import { UnsubscribableComponent } from '../../classes/abstract/unsubscribable-component';
import { IFilterItem } from '../../interfaces/IFilterItem';
import { ActivatedRoute } from '@angular/router';
import { Chip } from './chip.interface';
import { TranslateService } from '@ngx-translate/core';
import { State } from 'src/app/store/app.state';
import { Store } from '@ngrx/store';
import { selectProperties } from 'src/app/modules/properties/store/properties.selector';
import { CompaniesService } from 'src/app/modules/companies/companies.service';
import { Company } from 'src/app/shared/interfaces/company.interface';
import { Property } from 'src/app/shared/interfaces/property.interface';
import { PropertyUnit } from 'src/app/shared/interfaces/property-unit.interface';
import { User } from 'src/app/shared/interfaces/user';
import { selectAppUser } from '../../../store/app.selectors';
import { APP_USER_STATUS } from '../../../shared/constants';

interface FilterTags {
  c?: number[];
  p?: number[]; 
  u?: PropertyUnit[]; 
  s?: string;
}
/**
 * Reusable filtering component for index pages
 * - Property Filter, Unit Filter, Status Filter (Customizable)
 * - Removeable Filters Chips, Show all/Show less button
 */
@Component({
  selector: 'table-filters-bar',
  templateUrl: './table-filters-bar.component.html',
  styleUrls: ['./table-filters-bar.component.scss']
})
export class TableFiltersBarComponent extends UnsubscribableComponent implements OnInit {
  authUser: User | null;

  @Input() filterTag: string; // in order to label the filters in localStorage
  @Input() enablePropertyFilter = false;
  @Input() enableUnitFilter = false;
  @Input() enableStatusFilter = false;
  @Input() enableCompanyFilter = false;

  @Input() customFilters: {key: string, name: string}[] = [];
  @Input() externalChips: BehaviorSubject<Chip[]> = new BehaviorSubject<Chip[]>([]);

  // search bpx form control
  searchFieldControl = new FormControl<string>('');
  selectedFilter = 't';

  // property filter form control
  propertyList = new BehaviorSubject<Property[]>([]);
  propertyFilterControl = new FormControl<number[]>([]);
  propertiesTranspiler = (item: Property) => ({ value: item.id, label: item.name, data: item.address });

  // unit filter form control
  unitList = new BehaviorSubject<PropertyUnit[]>([]);
  unitFilterControl = new FormControl<PropertyUnit[]>([]);
  unitsTranspiler = (item: PropertyUnit) => ({
    value: item,
    label: "Unit " + item.unit + " - " + item.property_name,
  });

  // status filter form control
  // parent component pass in custom status list
  @Input() statusList: {value: string; label: string}[] = [];
  statusFilterControl = new FormControl<string>("enabled");

  // company filter form control
  companyList = new BehaviorSubject<Company[]>([]);
  companyFilterControl = new FormControl<number[]>([]);
  companiesTranspiler = (item: Company) => ({ value: item.id, label: item.name });

  /**
   * aggregate filters and search box data into a common data source
   * consumed internally and externally
   * consumers:
   * - Used in chips bar UI to display filters selected
   * - Emit to parent component to construct post request payload
   */
   // Bind selectedProperties, selectedUnits, selectedStatus, and selectedCompanies directly to form controls
  private selectedProperties = this.propertyFilterControl.valueChanges.pipe(
    startWith(this.propertyFilterControl.value), // Emit the initial value
    distinctUntilChanged()
  );

  private selectedUnits = this.unitFilterControl.valueChanges.pipe(
    startWith(this.unitFilterControl.value), // Emit the initial value
    distinctUntilChanged(),
  );

  private selectedStatus = this.statusFilterControl.valueChanges.pipe(
    startWith(this.statusFilterControl.value), // Emit the initial value
    distinctUntilChanged()
  );

  private selectedCompanies = this.companyFilterControl.valueChanges.pipe(
    startWith(this.companyFilterControl.value),
    distinctUntilChanged()
  );

  private generalFilter = new BehaviorSubject<{value: string, filter: string}[]>([]);

  private filtersAggregated = combineLatest([
    this.selectedProperties,
    this.selectedUnits,
    this.selectedStatus,
    this.selectedCompanies,
    this.generalFilter,
  ]).pipe(
    tap(([properties, units, status, companies, generalFilter]) => {
      localStorage.setItem('filter_tags_' + this.filterTag, JSON.stringify({
        tags: {
          p: properties,
          u: units,
          s: status,
          c: companies,
        } as FilterTags
      }));
    }),
    map(([properties, units, status, companies, generalFilter]) => {
      const filters = [].concat(
        ...generalFilter.map((filter, i) => ({ id: i, key: filter.filter, value: filter.value })),
        ...properties.map((value, i) => ({ id: i, key: "p", value: value })),
        ...units.map((value, i) => ({ id: i, key: "u", value: value.id })),
        ...companies.map((value, i) => ({ id: i, key: "c", value: value })),
      );

      if(this.enableStatusFilter) {
        filters.push({ id: 0, key: "s", value: status });
      }
      return filters;
    }),
    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
    shareReplay(1)
  );
  @Output() filtersAggregatedOutput = new EventEmitter<IFilterItem[]>();
  @Output() externalChipRemoved = new EventEmitter<Chip>();

  /**
   * construct chips data using the common aggregated data
   * flags to determine UI display mechanism
   * (numbers to show, is exceed one line, show all or hide all)
   */
  @ViewChild('filterContainer') filterContainer!: ElementRef;
  isRemoving = false;
  showAllChips = true;
  isChipsExceedOneLine = false;
  numChipsInFirstLine = 0;
  filtersChipsData: BehaviorSubject<Chip[]> = new BehaviorSubject<Chip[]>([]);

  isChipHidden(index: number) {
    return !this.showAllChips && index >= this.numChipsInFirstLine;
  }

  constructor(
    private store: Store<State>,
    private route: ActivatedRoute,
    private router: Router,
    private t: TranslateService,
    private companiesService: CompaniesService,
  ) {
    super();
    this.setUpFilters();
  }

  // fetch data and set up filters with its observables
  private setUpFilters() {
    this.sub = this.store.select(selectAppUser).subscribe(user => this.authUser = user !== APP_USER_STATUS.NOT_AUTHORIZED ? user : null);

    if (this.authUser?.permissions == 'payquad_admin') {
      this.companiesService.getCompanies().subscribe({
        next: res => this.companyList.next(res.data.map(c => ({id: c.id, name: c.name}))),
        error: err => console.error(err)
      });

      //Fill PropertyList with properties from selected companies
      combineLatest([
        this.store.select(selectProperties),
        this.companyFilterControl.valueChanges.pipe(startWith(this.companyFilterControl.value)),
      ]).subscribe(([allProperties, selectedCompanyIds]) => {
        if (selectedCompanyIds?.length) {
          const filtered = allProperties.filter(p => selectedCompanyIds.includes(p.company_id));
          this.propertyList.next(filtered);
        } else {
          this.propertyList.next(allProperties);
        }
      });

    } else {
      this.sub = this.store.select(selectProperties).subscribe(data => {
        this.propertyList.next(data);
      });
    }


    // Fill UnitList with units from selected properties
    combineLatest([
      this.propertyFilterControl.valueChanges,
      this.propertyList, // load units again after properties loaded
    ])
    .pipe(
      map(([selectedProperties, allProperties]) => {
        return [].concat(
          ...allProperties
            .filter(i => selectedProperties.includes(i.id))
            .map(property => property.property_units)
        );
      })
    )
    .subscribe((units) => {
      this.unitList.next(units);
    });

    this.filtersAggregated.subscribe(filters => {
      this.filtersAggregatedOutput.emit(filters);
      this.updateUrlWithFilters(filters);
    });
  }

  ngOnInit() {
    // Set custom filter value from URL query params or localStorage
    this.sub = this.route.queryParams.pipe(
      take(1)
    ).subscribe(params => {
      const filters: Record<string, any> = {};
      const localStorageFilters = this.getFiltersFromLocalStorage();
      let hasParams = false;

      // Initial filters: Merge URL params or fallback to localStorage
      const filterKeys = [... this.customFilters.map(f => f.key), 'p', 'u', 's', 'c'];

      filterKeys.forEach(key => {
        if (params.hasOwnProperty(key)) {
          hasParams = true;
          filters[key] = Array.isArray(params[key]) ? params[key] : [params[key]];
        } else if (localStorageFilters[key]){
          filters[key] = localStorageFilters[key];
        }
      });

      if (filters['p']) {
        this.propertyFilterControl.patchValue(filters['p']);
      }

      if (filters['u']) {
        this.unitList.pipe(
          filter(units => units.length > 0), // Skip the initial empty array
          take(1)
        ).subscribe(units => {
          const previouslySelectedUnitIds = new Set(filters['u'].map(u => typeof u === 'object' ? u.id : u));
          const selectUnits = units.filter(unit => previouslySelectedUnitIds.has(unit.id));

          this.unitFilterControl.patchValue(selectUnits);
        })
      }
      if (filters['s']) {
        this.statusFilterControl.patchValue(filters['s']);
      }

      if (this.authUser?.permissions == 'payquad_admin' && filters['c']) {
        this.companyFilterControl.patchValue(filters['c']);
      }

      const generalItems = this.customFilters
      .filter(f => filters[f.key])
      .map(f => ({ filter: f.key, value: filters[f.key][0] })); // assuming custom filters are single value

      this.generalFilter.next(generalItems);
    });
  }

  ngAfterViewInit() {
    this.setupFiltersChipsData();
    this.setupChipsDisplayMechanism();
  }

  private setupFiltersChipsData(): void {
    combineLatest([
      this.filtersAggregated, 
      this.externalChips,
      this.propertyList, // load chips again after properties loaded
      this.unitList, // load chips again after units loaded
      this.companyList, //load chips again after companies loaded
    ])
      .pipe(
        map(this.mapFiltersToChips.bind(this)),
      )
      .subscribe((data: Chip[]) => this.filtersChipsData.next(data));
  }

  private mapFiltersToChips([filterAggregated, externalChips]: [IFilterItem[], Chip[]]): Chip[] {
    const customKeys = this.customFilters.map(item => item.key);
    const searchChips = this.getSearchChips(filterAggregated, customKeys);
    const propertyChips = this.getPropertyChips(filterAggregated);
    const unitChips = this.getUnitChips();
    const companyChips = this.getCompanyChips(filterAggregated);
    // const statusChips = this.getStatusChip(filterAggregated); // single select, no need for chip

    return [...searchChips, ...propertyChips, ...unitChips, ...companyChips, ...externalChips];
  }

  private getSearchChips(filterAggregated: IFilterItem[], customKeys: string[]): Chip[] {
    const t = filterAggregated.filter(i => i.key === "t");
    const otherFilters = filterAggregated.filter(i => customKeys.includes(i.key));
    const hasKeyT = this.customFilters.some(item => item.key === 't');

    return [
      ...(hasKeyT ? [] : t.map(i => ({ ...i, id: i.id, label: i.value }))),
      ...otherFilters.map(i => ({
        id: i.id,
        key: i.key,
        label: `${i.value}`,
        value: `${i.value}`,
        filterName: `${this.customFilters.find(f => f.key === i.key)?.name}`,
      })),
    ];
  }

  private getPropertyChips(filterAggregated: IFilterItem[]): Chip[] {
    const p = filterAggregated.filter(i => i.key === "p");
    const propertiesIds = p.map(i => i.value);
    return this.propertyList.value
      .filter(i => propertiesIds.includes(i.id))
      .map(property => ({
        id: property.id,
        key: "p",
        label: property.name,
        value: property.id,
      }));
  }

  private getUnitChips(): Chip[] {
    const selectedUnits = this.unitFilterControl.value;

    return selectedUnits
      .map(unit => ({
        id: unit.id,
        key: "u",
        label: unit.property_name + " Unit " + unit.unit,
        value: unit, // unitFilterControl takes type PropertyUnit[]
      }));
  }

  private getStatusChip(filterAggregated: IFilterItem[]): Chip[] {
    if (!this.enableStatusFilter) return [];
    const s = filterAggregated.filter(i => i.key === "s");
    const selected = this.statusList.find(item => item.value === s[0]?.value);
    const label = selected?.label ?? s[0]?.value; // fallback to value if label not found

    return [{ id: s[0]?.id, key: "s", label: label, value: s[0]?.value }];
  }

  private getCompanyChips(filterAggregated: IFilterItem[]) : Chip[] {
    const c = filterAggregated.filter(i => i.key === "c");
    const companyIds = c.map(i => i.value);
    return this.companyList.value
      .filter(i => companyIds.includes(i.id))
      .map(company => ({
        id:company.id,
        key:"c",
        label:company.name,
        value:company.id,
      }));
  }

  private setupChipsDisplayMechanism(): void {
    this.filtersChipsData.subscribe(d => {
      const element = this.filterContainer.nativeElement;
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      ctx.font = '14px Roboto';

      const chipWidths = d.map(chip => ctx.measureText(chip.label).width + 86);
      const containerWidth = element.clientWidth;
      const { numChipsInFirstLine } = chipWidths.reduce((acc, val, index) => {
        if (acc.accumulatedWidth + val <= containerWidth) {
          acc.accumulatedWidth += val;
          acc.numChipsInFirstLine = index + 1;
        }
        return acc;
      }, { accumulatedWidth: 0, numChipsInFirstLine: 0 });

      this.numChipsInFirstLine = numChipsInFirstLine;
      this.isChipsExceedOneLine = d.length > this.numChipsInFirstLine;
      if (!this.isRemoving) {
        this.showAllChips = !this.isChipsExceedOneLine;
      }
      this.isRemoving = false;
    });
  }

  private updateUrlWithFilters(filters: IFilterItem[]) : void {
    const queryParams: { [key:string]: any } = {};
    filters.forEach(filter => {
      if (queryParams[filter.key]) {
        queryParams[filter.key] = [].concat(queryParams[filter.key], filter.value);
      } else {
        queryParams[filter.key] = filter.value;
      }
    });

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams:queryParams,
      queryParamsHandling: 'merge',
    });
  }

  getFiltersFromLocalStorage(): FilterTags {
    let filters = JSON.parse(localStorage.getItem('filter_tags_' + this.filterTag));
    return filters?.tags || {};
  }

  handleSearch() {
    const searchValue = this.searchFieldControl.value;

    if(searchValue.length > 0){
      // Filter out any filters with the same filter name so don't double search same filter
      const filteredFilters = this.generalFilter.value.filter(
        filter => filter.filter !== this.selectedFilter
      );

      this.generalFilter.next([
        ...filteredFilters,
        {value: searchValue.toLowerCase(), filter: this.selectedFilter}
      ]);
    }

    this.searchFieldControl.patchValue("");
  }

  /**
   * Clear the form control for selected filter
   * Form control is the root data source,
   * Ckearing form conrol will clear both filter selection and data display
   *
   * @param filter the target filter to clear
   */
  handleRemoveFilter(filter: Chip): void {
    this.isRemoving = true;

    switch (filter.key) {
      case "s":
        this.clearStatusFilter();
        break;
      case "t":
        this.removeGeneralFilter(filter);
        break;
      case "p":
        this.removePropertyFilter(filter);
        break;
      case "u":
        this.removeUnitFilter(filter);
        break;
      case "c":
        this.removeCompanyFilter(filter);
        break;
      case "external":
        this.removeExternalFilter(filter);
        break;
      default:
        this.removeCustomFilter(filter);
        break;
    }
  }

  private clearStatusFilter(): void {
    this.statusFilterControl.patchValue("");
  }

  private removeGeneralFilter(filter: Chip): void {
    const customKeys = this.customFilters.map(item => item.key);
    const filteredFilters = this.filtersChipsData.getValue()
      .filter(i => (i.key === "t" || customKeys.includes(i.key)) && i.id !== filter.id)
      .map(i => ({ value: i.value, filter: i.key }));
    this.generalFilter.next(filteredFilters);
  }

  private removePropertyFilter(filter: Chip): void {
    const filteredProperties = this.filtersChipsData.getValue()
      .filter(i => i.key === "p" && i.id !== filter.id)
      .map(i => Number(i.value));
    this.propertyFilterControl.patchValue(filteredProperties);
  }

  private removeUnitFilter(filter: Chip): void {
    const filteredUnits = this.filtersChipsData.getValue()
      .filter(i => i.key === "u" && i.id !== filter.id)
      .map(i => i.value);
    this.unitFilterControl.patchValue(filteredUnits);
  }

  private removeCompanyFilter(filter: Chip) : void {
    const filteredCompanies = this.filtersChipsData.getValue()
      .filter(i => i.key === "c" && i.id !== filter.id)
      .map(i => Number(i.value));
    this.companyFilterControl.patchValue(filteredCompanies);
  }

  private removeExternalFilter(filter: Chip): void {
    const filteredExternalChips = this.filtersChipsData.getValue()
      .filter(i => i.key === "external" && i.id !== filter.id);
    this.externalChips.next(filteredExternalChips);
    this.externalChipRemoved.emit(filter);
  }

  private removeCustomFilter(filter: Chip): void {
    const customKeys = this.customFilters.map(item => item.key);
    const filteredFilters = this.filtersChipsData.getValue()
      .filter(i => (i.key === "t" || customKeys.includes(i.key)) && i.id !== filter.id)
      .map(i => ({ value: i.value, filter: i.key }));
    this.generalFilter.next(filteredFilters);
  }

  handleClearFilters(): void {
    this.selectedFilter = 't'; 
    this.statusFilterControl.patchValue("");
    this.generalFilter.next([]);
    this.propertyFilterControl.patchValue([]);
    this.unitFilterControl.patchValue([]);
    this.companyFilterControl.patchValue([]);

    this.externalChips.value.forEach(value => {
      if (value.value != 'today' && value.value != 'new') // today and new will automatically be removed when filters are cleared
        this.externalChipRemoved.emit(value)
    });
    this.externalChips.next([]);
  }
}
